"use client"

/**
 * Ask the model for categories.
 *
 * The model is the reader: it reads the whole question and places it, only ever
 * from the catalog it is handed (any other id is dropped). The keyword reading
 * answers instantly while the model thinks, and stands in only if the model
 * cannot be reached. `status` says which of those moments this is.
 */

import * as React from "react"

import { suggestQuestionCategories } from "@/lib/leo-question-category-suggestion"
import { stemReadyForCategorySuggestions, type ExamCategory } from "@/lib/exam-categories"

export type ModelFit = "high" | "medium" | "low"
export type ModelPick = { categoryId: string; reason: string; fit: ModelFit }
export type ModelStatus = "idle" | "pending" | "answered" | "unavailable"

const SETTLE_MS = 900

export function useLeoCategoryPicks(stem: string, catalog: ExamCategory[]): { picks: ModelPick[]; status: ModelStatus } {
  const [state, setState] = React.useState<{ key: string; picks: ModelPick[]; status: ModelStatus }>({ key: "", picks: [], status: "idle" })
  const key = `${stem}|${catalog.length}`
  const ready = stemReadyForCategorySuggestions(stem) && catalog.length > 0

  React.useEffect(() => {
    if (!ready) return
    let cancelled = false
    setState((current) => (current.key === key ? current : { key, picks: [], status: "pending" }))
    const timer = window.setTimeout(() => {
      suggestQuestionCategories({ stem, categories: catalog })
        .then((found) => {
          if (cancelled) return
          const ids = new Set(catalog.map((category) => category.id))
          setState({
            key,
            status: "answered",
            picks: found.filter((row) => ids.has(row.id)).map((row) => ({ categoryId: row.id, reason: row.reason, fit: row.fit })),
          })
        })
        .catch(() => {
          if (!cancelled) setState({ key, picks: [], status: "unavailable" })
        })
    }, SETTLE_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [key, ready, stem, catalog])

  // An answer to an earlier stem is not an answer to this one.
  if (!ready || state.key !== key) return { picks: [], status: ready ? "pending" : "idle" }
  return { picks: state.picks, status: state.status }
}
